import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import './MyLeavePanel.css';

function apiUrl(path) {
  const b = String(getApiBaseUrl() || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

/** localStorage에서 세션/JWT 토큰을 읽어 Authorization 헤더 반환 */
function getAuthHeaders() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return {};
    const u = JSON.parse(raw);
    const tok = String(u?.token || '').trim();
    if (!tok) return {};
    return { Authorization: `Bearer ${tok}` };
  } catch { return {}; }
}

/** fetch with Authorization header */
function apiFetch(url, opts = {}) {
  const headers = Object.assign({}, getAuthHeaders(), opts.headers || {});
  return fetch(url, { ...opts, headers });
}

/* ── 연차 부여 및 정산 기준 안내 박스 ── */
function LeaveGuide() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ margin: '0 0 12px', borderRadius: 10, border: '1px solid #bae6fd', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#f0f9ff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0369a1',
      }}>
        <span>📋 연차 부여 및 정산 기준 안내</span>
        <span style={{ fontSize: 11, color: '#7dd3fc' }}>{open ? '▲ 닫기' : '▼ 펼치기'}</span>
      </button>
      {open && (
        <div style={{ padding: '12px 16px', background: '#fff', fontSize: 13, color: '#334155', lineHeight: 1.8, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, color: '#0369a1', marginBottom: 4 }}>1. 연차 부여 및 정산 기준</div>
            <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
              <li><b>연차 부여</b>: 1년 미만 근로자는 <b>12월 31일자</b>를 기준으로 연차가 부여됩니다.</li>
              <li><b>퇴사 시 정산 (월차 기준)</b>: 근속 1년 미만으로 중도 퇴사할 경우, 근로기준법에 따른 법정 발생 연차 <span style={{ color: '#0891b2' }}>(1개월 개근 시 1일 발생)</span>를 기준으로 최종 정산합니다.</li>
            </ul>
          </div>
          <div>
            <div style={{ fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>2. 초과 사용 연차 급여 차감 안내</div>
            <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
              <li>1년 미만으로 퇴사 시 선부여된 연차 중 실제 발생한 월차(근무 개월 수)를 <b>초과하여 사용한 연차</b>가 있는 경우,</li>
              <li>해당 초과분은 <b style={{ color: '#dc2626' }}>최종 급여에서 차감(환수)</b> 처리됨을 알려드립니다.</li>
            </ul>
          </div>
          <div>
            <div style={{ fontWeight: 800, color: '#6d28d9', marginBottom: 4 }}>3. ERP 연차 일수 표시 기준</div>
            <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
              <li>ERP상 표시되는 연차 일수는 편의상 <b>1월 1일자</b> 기준으로 계산된 값입니다.</li>
              <li>실제 연차는 위 <b>1항</b>(12월 31일 기준 부여)과 <b>2항</b>(월차 기준 정산)에 의해 계산됩니다.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

const LEAVE_ERR_KO = {
  dept_not_assigned: '소속 부서가 등록되어 있지 않습니다. ERP 부서관리에서 부서를 배정해 주세요.',
  dept_leader_not_set: '부서장이 지정되어 있지 않습니다. 부서 노드에 부서장을 지정해 주세요.',
  dept_leader_email_missing: '부서장 계정 이메일을 찾을 수 없습니다.',
  account_not_linked: '시스템에 연결된 계정이 없습니다. 관리자에게 문의하세요.',
  pending_only_for_self: '본인 명의로만 결재 신청할 수 있습니다.',
  'start_date > end_date': '시작일이 종료일보다 늦습니다.',
};

function formatLeaveApiError(data) {
  const d = data?.detail;
  if (typeof d === 'string') return LEAVE_ERR_KO[d] || d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join(' ');
  return data?.message || '요청에 실패했습니다.';
}

/** JSON이 아닌 HTML/프록시 오류 본문에서도 안전하게 파싱 */
async function readApiJson(res) {
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {
      _nonJson: true,
      _preview: String(text || '').replace(/\s+/g, ' ').trim().slice(0, 280),
    };
  }
  return { ok: res.ok, status: res.status, data };
}

/** YYYY-MM-DD → 로컬 날짜 (UTC 파싱으로 하루 밀림 방지) */
function parseYmdLocal(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1], 10);
  const mo = Number(m[2], 10);
  const d = Number(m[3], 10);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** 시작~종료 포함 일수 (백엔드 erp_leave._calc_use_days 의 annual/sick 와 동일) */
function inclusiveCalendarDays(startYmd, endYmd) {
  const s = parseYmdLocal(ymdNorm(startYmd));
  const e = parseYmdLocal(ymdNorm(endYmd));
  if (!s || !e) return null;
  if (e < s) return null;
  return Math.floor((e - s) / 86400000) + 1;
}

/**
 * 차감 일수 자동값 — 백엔드 _calc_use_days 와 맞춤
 * 반차 0.5일. 공가는 연차 미차감(0). 그 외는 기간 포함 일수만큼 연차 소진.
 */
function computeAutoUseDays(leaveType, startDate, endDate) {
  if (leaveType === 'half_am' || leaveType === 'half_pm') {
    return startDate ? 0.5 : '';
  }
  if (leaveType === 'public') {
    if (!startDate || !endDate) return '';
    return 0;
  }
  if (!startDate || !endDate) return '';
  const n = inclusiveCalendarDays(startDate, endDate);
  if (n == null) return '';
  return Math.max(n, 1);
}

function withAutoUseDays(partial) {
  const f = { ...partial };
  const isHalf = f.leave_type === 'half_am' || f.leave_type === 'half_pm';
  if (isHalf) {
    f.use_days = computeAutoUseDays(f.leave_type, f.start_date, f.end_date);
    return f;
  }
  const raw = Array.isArray(f.selected_dates) ? f.selected_dates : [];
  const list = [...new Set(raw.map(ymdNorm).filter(Boolean))].sort();
  if (list.length) {
    f.selected_dates = list;
    f.start_date = list[0];
    f.end_date = list[list.length - 1];
    f.use_days = f.leave_type === 'public' ? 0 : list.length;
  } else {
    f.use_days = computeAutoUseDays(f.leave_type, f.start_date, f.end_date);
  }
  return f;
}

/** 정렬된 YYYY-MM-DD 목록 → 연속 구간 [start,end][] (각 구간별 API 1건) */
function splitSortedDatesToRanges(sorted) {
  const d = sorted.filter(Boolean).sort();
  if (!d.length) return [];
  const out = [];
  let a = d[0];
  let b = d[0];
  for (let i = 1; i < d.length; i += 1) {
    const cur = d[i];
    const prev = d[i - 1];
    const p = parseYmdLocal(prev);
    const c = parseYmdLocal(cur);
    if (!p || !c) {
      out.push([a, b]);
      a = cur;
      b = cur;
      continue;
    }
    const next = new Date(p);
    next.setDate(next.getDate() + 1);
    if (toYmdFromDate(next) === cur) {
      b = cur;
    } else {
      out.push([a, b]);
      a = cur;
      b = cur;
    }
  }
  out.push([a, b]);
  return out;
}

function toYmdFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 시작~종료(포함) 구간의 모든 YYYY-MM-DD (기존 휴가 일자 막기용) */
function expandYmdRangeInclusive(startYmd, endYmd) {
  const s = parseYmdLocal(ymdNorm(startYmd));
  const e = parseYmdLocal(ymdNorm(endYmd));
  if (!s || !e || e < s) return [];
  const out = [];
  const cur = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  while (cur <= end) {
    out.push(toYmdFromDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** 결재대기·승인 등 이미 잡힌 날(반려 제외) */
function blockedYmdSetFromLeaveList(items) {
  const set = new Set();
  for (const it of items || []) {
    if (String(it.status || '') === 'rejected') continue;
    expandYmdRangeInclusive(it.start_date, it.end_date).forEach((y) => set.add(y));
  }
  return set;
}

function todayYmdLocal() {
  return toYmdFromDate(new Date());
}

/** 비교·표시용 YYYY-MM-DD (ISO datetime 등은 앞 10자만) */
function ymdNorm(x) {
  if (x == null || x === '') return '';
  const s = String(x).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function cmpYmd(a, b) {
  const A = ymdNorm(a);
  const B = ymdNorm(b);
  if (!A || !B) return 0;
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function buildMonthCells(year, month) {
  const first = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const pad = first.getDay();
  const out = [];
  for (let i = 0; i < pad; i += 1) out.push({ kind: 'pad' });
  for (let d = 1; d <= lastDay; d += 1) {
    const ymd = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    out.push({ kind: 'day', ymd, d });
  }
  return out;
}

const WEEK_KO = ['일', '월', '화', '수', '목', '금', '토'];

/** 연차·병가 등: 쉬고 싶은 날짜를 날마다 클릭해 토글 (5일·7일처럼 비연속 가능) */
function LeavePickDaysCalendar({ selectedDates, onToggleDay, blockedYmds }) {
  const tMin = todayYmdLocal();
  const sorted = useMemo(
    () => [...new Set((selectedDates || []).map(ymdNorm).filter(Boolean))].sort(),
    [selectedDates],
  );
  /* 표시 월은 ‹ › 로만 바꿈. 선택 목록의 '가장 이른 날'로 자동 점프하면 4월+5월 같이 고를 때 5월에서 클릭해도 4월로 튕김 */
  const [vy, setVy] = useState(() => new Date().getFullYear());
  const [vm, setVm] = useState(() => new Date().getMonth() + 1);

  const pickedSet = useMemo(() => new Set(sorted), [sorted]);
  const blockedSet = useMemo(() => {
    if (blockedYmds instanceof Set) return blockedYmds;
    return new Set(Array.isArray(blockedYmds) ? blockedYmds : []);
  }, [blockedYmds]);
  const cells = useMemo(() => buildMonthCells(vy, vm), [vy, vm]);

  const shiftMonth = (delta) => {
    const d = new Date(vy, vm - 1 + delta, 1);
    setVy(d.getFullYear());
    setVm(d.getMonth() + 1);
  };

  const title = `${vy}년 ${vm}월`;

  return (
    <div className="ml-cal">
      <div className="ml-cal__nav">
        <button type="button" className="ml-cal__nav-btn" onClick={() => shiftMonth(-1)}>
          ‹
        </button>
        <span className="ml-cal__title">{title}</span>
        <button type="button" className="ml-cal__nav-btn" onClick={() => shiftMonth(1)}>
          ›
        </button>
      </div>
      <p className="ml-cal__hint">
        <strong>쉬고 싶은 날</strong>을 누르면 선택됩니다. 이미 고른 날·<strong>이미 신청·결재 중인 날</strong>은 달력에서 눌러도 바뀌지 않습니다(해제는 아래 칩 × 또는 전체 해제).
        <span className="ml-cal__hint-sub"> 5월 5일·7일처럼 띄엄 선택 가능. 다른 달은 ‹ › 로 이동하세요.</span>
      </p>
      <div className="ml-cal__week">
        {WEEK_KO.map((w) => (
          <div key={w} className="ml-cal__weekday">
            {w}
          </div>
        ))}
      </div>
      <div className="ml-cal__grid">
        {cells.map((c, idx) => {
          if (c.kind === 'pad') return <div key={`p-${idx}`} className="ml-cal__pad" />;
          const { ymd, d } = c;
          const past = cmpYmd(ymd, tMin) < 0;
          const blocked = !past && blockedSet.has(ymd);
          const picked = !past && !blocked && pickedSet.has(ymd);
          let cellCls = 'ml-cal__day';
          if (past) cellCls += ' ml-cal__day--past';
          else if (blocked) cellCls += ' ml-cal__day--blocked';
          else if (picked) cellCls += ' ml-cal__day--picked';
          const dtCell = parseYmdLocal(ymd);
          const dow = dtCell ? dtCell.getDay() : 0;
          const sun = dow === 0;
          const sat = dow === 6;
          if (sun && !past && !blocked) cellCls += ' ml-cal__day--sun';
          if (sat && !past && !blocked) cellCls += ' ml-cal__day--sat';
          return (
            <button
              key={ymd}
              type="button"
              className={cellCls}
              disabled={past || blocked}
              title={blocked ? '이미 휴가가 신청된 날입니다' : undefined}
              onClick={() => {
                if (past || blocked || picked) return;
                onToggleDay(ymdNorm(ymd));
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 반차: 달력에서 하루만 클릭 */
function LeaveSingleDayCalendar({ valueYmd, onPickDay, blockedYmds }) {
  const tMin = todayYmdLocal();
  const init = parseYmdLocal(ymdNorm(valueYmd)) || new Date();
  const [vy, setVy] = useState(() => init.getFullYear());
  const [vm, setVm] = useState(() => init.getMonth() + 1);

  useEffect(() => {
    const s = parseYmdLocal(ymdNorm(valueYmd));
    if (s) {
      setVy(s.getFullYear());
      setVm(s.getMonth() + 1);
    }
  }, [valueYmd]);

  const blockedSet = useMemo(() => {
    if (blockedYmds instanceof Set) return blockedYmds;
    return new Set(Array.isArray(blockedYmds) ? blockedYmds : []);
  }, [blockedYmds]);

  const cells = useMemo(() => buildMonthCells(vy, vm), [vy, vm]);

  const shiftMonthHalf = (delta) => {
    const d = new Date(vy, vm - 1 + delta, 1);
    setVy(d.getFullYear());
    setVm(d.getMonth() + 1);
  };

  return (
    <div className="ml-cal ml-cal--compact">
      <div className="ml-cal__nav">
        <button type="button" className="ml-cal__nav-btn" onClick={() => shiftMonthHalf(-1)}>
          ‹
        </button>
        <span className="ml-cal__title">{`${vy}년 ${vm}월`}</span>
        <button type="button" className="ml-cal__nav-btn" onClick={() => shiftMonthHalf(1)}>
          ›
        </button>
      </div>
      <p className="ml-cal__hint">
        날짜 한 번 클릭하면 반차 적용일로 설정됩니다. 이미 휴가가 있는 날은 선택할 수 없습니다.
      </p>
      <div className="ml-cal__week">
        {WEEK_KO.map((w) => (
          <div key={w} className="ml-cal__weekday">
            {w}
          </div>
        ))}
      </div>
      <div className="ml-cal__grid">
        {cells.map((c, idx) => {
          if (c.kind === 'pad') return <div key={`hp-${idx}`} className="ml-cal__pad" />;
          const { ymd, d } = c;
          const past = cmpYmd(ymd, tMin) < 0;
          const blocked = !past && blockedSet.has(ymd);
          const sel = !blocked && ymdNorm(valueYmd) && ymd === ymdNorm(valueYmd);
          let cellCls = 'ml-cal__day';
          if (past) cellCls += ' ml-cal__day--past';
          else if (blocked) cellCls += ' ml-cal__day--blocked';
          else if (sel) cellCls += ' ml-cal__day--edge';
          const dtCell = parseYmdLocal(ymd);
          const dow = dtCell ? dtCell.getDay() : 0;
          if (!past && !blocked && dow === 0) cellCls += ' ml-cal__day--sun';
          if (!past && !blocked && dow === 6) cellCls += ' ml-cal__day--sat';
          return (
            <button
              key={ymd}
              type="button"
              className={cellCls}
              disabled={past || blocked}
              title={blocked ? '이미 휴가가 신청된 날입니다' : undefined}
              onClick={() => !past && !blocked && onPickDay(ymdNorm(ymd))}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 내 급여 및 연차: 본인 연차 조회·신청 (수동 조정은 조회만, 휴가 내역 삭제 불가)
 */
export default function MyLeavePanel({ email, displayName }) {
  const [leaveYear, setLeaveYear] = useState(() => new Date().getFullYear());
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveAdjusts, setLeaveAdjusts] = useState([]);
  const [leaveList, setLeaveList] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveForm, setLeaveForm] = useState(null);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [leaveRequestErr, setLeaveRequestErr] = useState('');
  const [leaveLoadErr, setLeaveLoadErr] = useState('');

  const fetchLeaveData = useCallback(async (em, year) => {
    if (!em) return;
    setLeaveLoading(true);
    setLeaveLoadErr('');
    try {
      const [bRes, lRes, aRes] = await Promise.all([
        apiFetch(apiUrl(`/api/erp_leave/balance?email=${encodeURIComponent(em)}&year=${year}`)),
        apiFetch(apiUrl(`/api/erp_leave/list?email=${encodeURIComponent(em)}&year=${year}`)),
        apiFetch(apiUrl(`/api/erp_leave/adjust?email=${encodeURIComponent(em)}&year=${year}`)),
      ]);
      const [b, l, a] = await Promise.all([readApiJson(bRes), readApiJson(lRes), readApiJson(aRes)]);
      const parts = [];
      [b, l, a].forEach((x, i) => {
        if (x.ok) return;
        const label = ['balance', 'list', 'adjust'][i];
        const d = x.data || {};
        const msg =
          typeof d.detail === 'string'
            ? d.detail
            : d._nonJson && d._preview
              ? d._preview
              : `HTTP ${x.status}`;
        parts.push(`${label}: ${msg}`);
      });
      if (parts.length) setLeaveLoadErr(parts.join(' · '));

      const bData = b.data;
      const lData = l.data;
      const aData = a.data;
      if (bData.success) setLeaveBalance(bData.balance);
      else setLeaveBalance(null);
      if (lData.success) setLeaveList(lData.items || []);
      else setLeaveList([]);
      if (aData.success) setLeaveAdjusts(aData.items || []);
      else setLeaveAdjusts([]);
    } catch (e) {
      console.error('연차 데이터 로드 실패', e);
      setLeaveLoadErr(String(e?.message || e));
      setLeaveBalance(null);
      setLeaveList([]);
      setLeaveAdjusts([]);
    } finally {
      setLeaveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (email) fetchLeaveData(email, leaveYear);
  }, [email, leaveYear, fetchLeaveData]);

  const blockedLeaveYmds = useMemo(() => blockedYmdSetFromLeaveList(leaveList), [leaveList]);

  const addLeaveRequest = async () => {
    if (!leaveForm || !email) return;
    setLeaveRequestErr('');
    setLeaveSaving(true);
    try {
      const isHalf = leaveForm.leave_type === 'half_am' || leaveForm.leave_type === 'half_pm';
      const leaveRest = { ...leaveForm };
      delete leaveRest.selected_dates;

      if (isHalf) {
        const useDays = computeAutoUseDays(
          leaveForm.leave_type,
          leaveForm.start_date,
          leaveForm.end_date,
        );
        const payload = {
          ...leaveRest,
          email,
          year: leaveYear,
          immediate: false,
          ...(useDays === '' ? {} : { use_days: useDays }),
        };
        const res = await apiFetch(apiUrl('/api/erp_leave/request'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (data.success) {
          setLeaveForm(null);
          fetchLeaveData(email, leaveYear);
        } else {
          setLeaveRequestErr(formatLeaveApiError(data));
        }
        return;
      }

      const dates = [...new Set((leaveForm.selected_dates || []).map(ymdNorm).filter(Boolean))].sort();
      if (!dates.length) {
        setLeaveRequestErr('쉬고 싶은 날짜를 달력에서 한 개 이상 선택해 주세요.');
        return;
      }
      const ranges = splitSortedDatesToRanges(dates);
      for (let ri = 0; ri < ranges.length; ri += 1) {
        const [s, e] = ranges[ri];
        const ud = computeAutoUseDays(leaveForm.leave_type, s, e);
        const payload = {
          ...leaveRest,
          start_date: s,
          end_date: e,
          email,
          year: leaveYear,
          immediate: false,
          ...(ud === '' ? {} : { use_days: ud }),
        };
        const res = await apiFetch(apiUrl('/api/erp_leave/request'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) {
          const extra =
            ranges.length > 1
              ? ` (${ri + 1}/${ranges.length}번째 구간 ${s}~${e} 처리 중 실패. 이전 구간은 이미 접수되었을 수 있습니다.)`
              : '';
          setLeaveRequestErr(`${formatLeaveApiError(data)}${extra}`);
          fetchLeaveData(email, leaveYear);
          return;
        }
      }
      setLeaveForm(null);
      fetchLeaveData(email, leaveYear);
    } catch (e) {
      setLeaveRequestErr(String(e?.message || e));
    } finally {
      setLeaveSaving(false);
    }
  };

  if (!email) {
    return (
      <div className="my-leave-panel my-leave-panel--empty">
        <p>연차 정보를 보려면 로그인된 계정이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="my-leave-panel">
      <div className="my-leave-panel__header">
        <div>
          <div className="my-leave-panel__title">연차 관리</div>
          <div className="my-leave-panel__sub">
            {displayName || '—'} ({email})
          </div>
        </div>
      </div>

      <LeaveGuide />

      <div className="my-leave-panel__year-row">
        {[leaveYear - 1, leaveYear, leaveYear + 1].map((y) => (
          <button
            key={y}
            type="button"
            onClick={() => setLeaveYear(y)}
            className={`my-leave-panel__year-btn${y === leaveYear ? ' my-leave-panel__year-btn--on' : ''}`}
          >
            {y}년
          </button>
        ))}
        <button
          type="button"
          className="my-leave-panel__refresh"
          onClick={() => fetchLeaveData(email, leaveYear)}
        >
          새로고침
        </button>
      </div>

      {leaveLoadErr ? (
        <div
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <strong>연차 API 오류</strong>
          <div style={{ marginTop: 6, fontWeight: 600 }}>{leaveLoadErr}</div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', opacity: 0.9 }}>
            서버(ERP) 로그 또는 관리자에게 위 메시지를 전달해 주세요. 재배포 후에도 동일하면 DB 스키마(연차·결재 테이블)를 확인합니다.
          </div>
        </div>
      ) : null}

      <div className="my-leave-panel__body salary-hide-scrollbar">
        {leaveLoading ? (
          <div className="my-leave-panel__loading">불러오는 중…</div>
        ) : (
          <>
            {leaveBalance && (
              <div className="my-leave-balance-card">
                <div className="my-leave-balance-card__label">연차 현황 ({leaveYear}년)</div>
                <div className="my-leave-badges">
                  {[
                    { label: '총 연차', val: `${leaveBalance.total_days}일`, cls: 't' },
                    { label: '수동조정', val: `${leaveBalance.adjust_days >= 0 ? '+' : ''}${leaveBalance.adjust_days}일`, cls: 'a' },
                    { label: '사용', val: `${leaveBalance.used_days}일`, cls: 'u' },
                    { label: '잔여', val: `${leaveBalance.remain_days}일`, cls: 'r' },
                  ].map(({ label, val, cls }) => (
                    <div key={label} className={`my-leave-badge my-leave-badge--${cls}`}>
                      <div className="my-leave-badge__l">{label}</div>
                      <div className="my-leave-badge__v">{val}</div>
                    </div>
                  ))}
                </div>
                <div className="my-leave-base-row">
                  <span className="my-leave-base-row__k">기본연차:</span>
                  <span className="my-leave-base-row__v">{leaveBalance.total_days}일</span>
                  <span className="my-leave-base-row__hint">※ 입사일 기준 자동계산 · 수동 조정은 인사 담당 처리분만 표시됩니다</span>
                </div>

                <div className="my-leave-adjust-section">
                  <div className="my-leave-adjust-head my-leave-adjust-head--readonly">
                    <span>수동 조정 이력</span>
                  </div>
                  <p className="my-leave-section-hint my-leave-section-hint--tight">
                    인사 담당자가 등록한 조정만 표시됩니다. 이 화면에서 추가·삭제할 수 없습니다.
                  </p>

                  {leaveAdjusts.length === 0 ? (
                    <div className="my-leave-muted-center">수동 조정 내역이 없습니다.</div>
                  ) : (
                    <div className="my-leave-adjust-list">
                      {leaveAdjusts.map((adj) => (
                        <div
                          key={adj.seq}
                          className={`my-leave-adjust-item${adj.amount >= 0 ? ' pos' : ' neg'}`}
                        >
                          <span className="my-leave-adjust-item__amt">
                            {adj.amount >= 0 ? '+' : ''}
                            {adj.amount}일
                          </span>
                          <span className="my-leave-adjust-item__reason">{adj.reason || '—'}</span>
                          <span className="my-leave-adjust-item__by">{adj.created_by}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="my-leave-section-head">
                <span>휴가·연차 내역</span>
                <button
                  type="button"
                  className="my-leave-btn my-leave-btn--green"
                  onClick={() =>
                    setLeaveForm(
                      withAutoUseDays({
                        leave_type: 'annual',
                        start_date: '',
                        end_date: '',
                        selected_dates: [],
                        use_days: '',
                        reason: '',
                        memo: '',
                      }),
                    )
                  }
                >
                  + 연차 신청
                </button>
              </div>
              <p className="my-leave-section-hint">
                연차·휴가 신청은 부서장 결재 후 반영됩니다. 승인 전까지 잔여일에 차감되지 않습니다.
                병가·특별휴가는 기간만큼 연차에서 차감됩니다. 공가는 연차에서 차감되지 않습니다.
              </p>
            </div>

            {leaveForm && (
              <LeaveRequestForm
                leaveForm={leaveForm}
                setLeaveForm={setLeaveForm}
                leaveSaving={leaveSaving}
                leaveRequestErr={leaveRequestErr}
                blockedLeaveYmds={blockedLeaveYmds}
                onSave={addLeaveRequest}
                onCancel={() => { setLeaveRequestErr(''); setLeaveForm(null); }}
              />
            )}

            {leaveList.length === 0 ? (
              <div className="my-leave-muted-center my-leave-muted-center--lg">등록된 휴가·연차 내역이 없습니다.</div>
            ) : (
              <div className="my-leave-table-wrap">
                <table className="my-leave-table">
                  <thead>
                    <tr>
                      {['종류', '기간', '차감', '상태', '사유'].map((h, i) => (
                        <th key={i} className={h === '차감' ? 'c' : ''}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leaveList.map((item) => (
                      <LeaveTableRow key={item.seq} item={item} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function leaveStatusPill(st) {
  const s = String(st || 'approved');
  if (s === 'pending') return { label: '결재대기', cls: 'my-leave-status--pending' };
  if (s === 'rejected') return { label: '반려', cls: 'my-leave-status--rejected' };
  if (s === 'approved') return { label: '승인', cls: 'my-leave-status--approved' };
  return { label: s, cls: 'my-leave-status--muted' };
}

function LeaveTableRow({ item }) {
  const typeColors = {
    annual: ['#e0f2fe', '#0369a1'],
    half_am: ['#fef9c3', '#854d0e'],
    half_pm: ['#fef9c3', '#854d0e'],
    sick: ['#fce7f3', '#9d174d'],
    special: ['#f0fdf4', '#166534'],
    public: ['#f5f3ff', '#5b21b6'],
  };
  const [bg, co] = typeColors[item.leave_type] || ['#f1f5f9', '#64748b'];
  const isSameDay = item.start_date === item.end_date;
  const halfTimeLabel =
    item.leave_type === 'half_am' ? '09:00~13:00' : item.leave_type === 'half_pm' ? '13:00~18:00' : null;
  const st = leaveStatusPill(item.status);
  const periodText = isSameDay ? item.start_date : `${item.start_date} ~ ${item.end_date}`;
  const isPublic = item.leave_type === 'public';
  const deductCell =
    item.status === 'approved' && isPublic ? (
      <span className="my-leave-use-zero">미차감</span>
    ) : item.status === 'approved' && item.use_days > 0 ? (
      <span className="my-leave-use-minus">-{item.use_days}일</span>
    ) : item.status === 'approved' ? (
      <span className="my-leave-use-zero">미차감</span>
    ) : (
      <span className="my-leave-use-pending">
        {isPublic ? '연차 미차감(예정)' : item.use_days > 0 ? `${item.use_days}일(예정)` : '—'}
      </span>
    );
  return (
    <tr className="my-leave-table__row">
      <td data-label="종류" className="my-leave-td--kind">
        <span className="my-leave-type-pill" style={{ background: bg, color: co }}>
          {item.type_label}
        </span>
      </td>
      <td data-label="기간" className="my-leave-td--period">
        <span className="my-leave-period-text">{periodText}</span>
        {halfTimeLabel ? <span className="my-leave-half-tag">{halfTimeLabel}</span> : null}
      </td>
      <td data-label="차감" className="my-leave-td--deduct c">
        {deductCell}
      </td>
      <td data-label="상태" className="my-leave-td--status">
        <span className={`my-leave-status-pill ${st.cls}`}>{st.label}</span>
      </td>
      <td data-label="사유" className="my-leave-td--reason my-leave-reason-cell">
        <div className="my-leave-reason-main">{item.reason || '—'}</div>
        {item.status === 'rejected' && String(item.reject_reason || '').trim() ? (
          <div className="my-leave-reject-reason" title="부서장이 입력한 반려 사유">
            <span className="my-leave-reject-reason__label">반려 사유</span>
            {String(item.reject_reason).trim()}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function LeaveRequestForm({
  leaveForm,
  setLeaveForm,
  leaveSaving,
  leaveRequestErr,
  blockedLeaveYmds,
  onSave,
  onCancel,
}) {
  const isHalf = leaveForm.leave_type === 'half_am' || leaveForm.leave_type === 'half_pm';
  const halfLabel = leaveForm.leave_type === 'half_am' ? '09:00 ~ 13:00' : '13:00 ~ 18:00';
  const pickedSorted = useMemo(
    () => [...new Set((leaveForm.selected_dates || []).map(ymdNorm).filter(Boolean))].sort(),
    [leaveForm.selected_dates],
  );
  const useDaysReady = isHalf
    ? computeAutoUseDays(leaveForm.leave_type, leaveForm.start_date, leaveForm.end_date)
    : pickedSorted.length
      ? leaveForm.leave_type === 'public'
        ? 0
        : pickedSorted.length
      : '';
  const useDaysDisplay =
    useDaysReady === '' ? '' : useDaysReady === 0 ? '0' : String(useDaysReady);
  return (
    <div className="my-leave-new-form">
      <div className="my-leave-new-form__title">연차·휴가 신청</div>
      {leaveRequestErr ? <div className="my-leave-form-err">{leaveRequestErr}</div> : null}
      <div className="my-leave-new-grid">
        <div>
          <div className="my-leave-field-label">휴가 종류</div>
          <select
            value={leaveForm.leave_type}
            onChange={(e) => {
              const lt = e.target.value;
              const isHalfNew = lt === 'half_am' || lt === 'half_pm';
              setLeaveForm((f) => {
                const wasHalf = f.leave_type === 'half_am' || f.leave_type === 'half_pm';
                if (isHalfNew) {
                  return withAutoUseDays({
                    ...f,
                    leave_type: lt,
                    end_date: f.start_date || '',
                    selected_dates: undefined,
                  });
                }
                return withAutoUseDays({
                  ...f,
                  leave_type: lt,
                  ...(wasHalf ? { start_date: '', end_date: '', selected_dates: [] } : {}),
                });
              });
            }}
            className="my-leave-select"
          >
            <option value="annual">연차</option>
            <option value="half_am">오전반차</option>
            <option value="half_pm">오후반차</option>
            <option value="sick">병가</option>
            <option value="special">특별휴가</option>
            <option value="public">공가</option>
          </select>
        </div>
        <div>
          <div className="my-leave-field-label">차감 일수</div>
          <input
            type="text"
            readOnly
            placeholder="날짜·종류 선택 시 자동"
            value={useDaysDisplay}
            title={
              isHalf
                ? '반차 날짜와 종류에 따라 자동 계산됩니다.'
                : leaveForm.leave_type === 'public'
                  ? '공가는 연차에서 차감되지 않습니다(0일).'
                  : '선택한 날짜 수만큼 차감됩니다(비연속 날도 각각 1일).'
            }
            className="my-leave-input my-leave-input--ro"
          />
        </div>
        {isHalf ? (
          <div className="my-leave-new-span2">
            <div className="my-leave-field-label">날짜 (달력에서 선택)</div>
            <LeaveSingleDayCalendar
              key={`half-cal-${leaveForm.leave_type}`}
              valueYmd={leaveForm.start_date}
              blockedYmds={blockedLeaveYmds}
              onPickDay={(ymd) =>
                setLeaveForm((f) => {
                  if (blockedLeaveYmds.has(ymd)) return f;
                  return withAutoUseDays({ ...f, start_date: ymd, end_date: ymd });
                })
              }
            />
            <div className="my-leave-half-row" style={{ marginTop: 8 }}>
              <input
                type="date"
                value={leaveForm.start_date}
                onChange={(e) => {
                  const d = e.target.value;
                  const n = ymdNorm(d);
                  if (n && blockedLeaveYmds.has(n)) return;
                  setLeaveForm((f) => withAutoUseDays({ ...f, start_date: d, end_date: d }));
                }}
                className="my-leave-input"
              />
              <span className="my-leave-half-badge">{halfLabel}</span>
            </div>
          </div>
        ) : (
          <div className="my-leave-new-span2">
            <div className="my-leave-field-label">날짜 (달력에서 선택 · 칩에서 해제)</div>
            <LeavePickDaysCalendar
              key={`pick-cal-${leaveForm.leave_type}`}
              selectedDates={leaveForm.selected_dates || []}
              blockedYmds={blockedLeaveYmds}
              onToggleDay={(ymd) =>
                setLeaveForm((f) => {
                  if (blockedLeaveYmds.has(ymd)) return f;
                  const cur = [...(f.selected_dates || [])].map(ymdNorm).filter(Boolean);
                  if (cur.includes(ymd)) return f;
                  cur.push(ymd);
                  return withAutoUseDays({ ...f, selected_dates: cur });
                })
              }
            />
            {pickedSorted.length > 0 ? (
              <div className="my-leave-picked-dates">
                <span className="my-leave-picked-dates__label">선택한 날</span>
                <div className="my-leave-picked-dates__chips">
                  {pickedSorted.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="my-leave-date-chip"
                      onClick={() =>
                        setLeaveForm((f) =>
                          withAutoUseDays({
                            ...f,
                            selected_dates: pickedSorted.filter((x) => x !== d),
                          }),
                        )
                      }
                    >
                      {d}
                      <span className="my-leave-date-chip__x" aria-hidden>
                        ×
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="my-leave-clear-dates"
                  onClick={() =>
                    setLeaveForm((f) => withAutoUseDays({ ...f, selected_dates: [] }))
                  }
                >
                  전체 해제
                </button>
              </div>
            ) : null}
          </div>
        )}
        <div className="my-leave-new-span2">
          <div className="my-leave-field-label">사유</div>
          <input
            placeholder="사유 입력"
            value={leaveForm.reason}
            onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))}
            className="my-leave-input"
          />
        </div>
      </div>
      <div className="my-leave-new-actions">
        <button type="button" className="my-leave-btn my-leave-btn--muted" onClick={onCancel}>
          취소
        </button>
        <button
          type="button"
          className="my-leave-btn my-leave-btn--green"
          disabled={
            leaveSaving ||
            useDaysReady === '' ||
            (isHalf ? !leaveForm.start_date : pickedSorted.length === 0)
          }
          onClick={onSave}
        >
          {leaveSaving ? '제출 중…' : '신청'}
        </button>
      </div>
    </div>
  );
}
